import {
  ReactElement, useCallback, useEffect, useRef, useState,
} from 'react';
import { MapContentContainer } from './styles';
import { MapChart } from './components/MapChart';
import { useFilterContext } from '../contexts/FilterContext';
import { useMap } from './hooks/useMap';
import { useDebounce } from '~/hooks';
import { UnitsInfo, useHealthDataContext } from '../contexts/HealthDataContext';
import { MapInfo } from './components/MapInfo';
import { useLatestRef } from './hooks/useLatestRef';
import { MapUnitInfo } from './types';
import { sameStatesArray } from './helpers';

interface MapContentProps {
  hasHealth: boolean;
}

interface UnitInfo {
  unitId: string,
  name: string,
  city: string,
}

export function MapContent({
  hasHealth,
}: Readonly<MapContentProps>): ReactElement {
  const { units } = useHealthDataContext();
  const {
    client, selectedOptions, onSelectOption, filterOptions,
    clearFilter,
  } = useFilterContext();
  const [hoveredState, setHoveredState] = useState<string>();
  const [selectedState, setSelectedState] = useState<string>();
  const [hoveredUnit, setHoveredUnit] = useState<UnitInfo>();
  const [selectedUnit, setSelectedUnit] = useState<UnitInfo>();
  const [unitsStateMap, setUnitsStateMap] = useState<Map<string, UnitsInfo>>(new Map<string, UnitsInfo>());
  const [allUnitsStateMap, setAllUnitsStateMap] = useState<Map<string, UnitsInfo>>(new Map<string, UnitsInfo>());
  const unitsStateMapReference = useRef<Map<string, UnitsInfo>>(new Map<string, UnitsInfo>());
  const [scope, setScope] = useState<'state' | 'unit'>('state');

  const debouncedHoverState = useDebounce(hoveredState, 200);
  const debouncedHoverUnit = useDebounce(hoveredUnit, 200);
  const debouncedScope = useDebounce(scope, 200);
  const debouncedSelectedState = useDebounce(selectedState, 200);
  const debouncedSelectedUnit = useDebounce(selectedUnit, 100);

  const firstRender = useRef(true);
  const prevUnit = useRef<string>();
  const prevClient = useRef<string>();
  const prevGroup = useRef<string>();
  const prevHealth = useRef<string>();
  const prevState = useRef<string[]>();

  const scopeRef = useLatestRef(scope);

  const {
    updateStateMap, goCountryMap, updateCountryMap, onCloseUnitCard,
  } = useMap({
    stateEvents: {
      onClick: (state) => onClickState(state, unitsStateMapReference.current),
      onPointerOut: onPointerOutState,
      onPointerOver: onPointerOverState,
    },
    unitEvents: {
      onClick: onClickUnit,
      onPointerOut: onPointerOutUnit,
      onPointerOver: onPointerOverUnit,
    },
    disabled: !hasHealth,
    onClickResetButton,
  });

  const updateUnitsMap = useCallback((map: Map<string, UnitsInfo>): void => {
    setUnitsStateMap(map);
    if (selectedState) {
      updateStateMap(selectedState, map);
    } else {
      updateCountryMap(map);
    }
  }, [selectedState, updateCountryMap, updateStateMap]);

  function handleScope(newScope: 'state' | 'unit'): void {
    if (newScope === scopeRef.current) return;

    setScope(newScope);
  }

  function onClickState(newState: string, map: Map<string, UnitsInfo>): void {
    handleScope('state');
    const foundState = filterOptions.states.find((item) => item.label === newState);
    if (!foundState) return;

    setSelectedState(newState);
    setHoveredState(undefined);
    clearFilter('state');
    onSelectOption('state', foundState.value);

    updateStateMap(newState, map);
  }

  function onPointerOutState(): void {
    handleScope('state');
    setHoveredState(undefined);
  }

  function onPointerOverState(newState: string): void {
    handleScope('state');
    setHoveredState(newState);
  }

  function onClickUnit(unitId: string, info: MapUnitInfo): void {
    handleScope('unit');
    setSelectedState(info.state);
    setSelectedUnit({
      unitId,
      name: info.name,
      city: info.city,
    });
    prevUnit.current = unitId;
  }

  function onPointerOverUnit(unitId: string, info: MapUnitInfo): void {
    handleScope('unit');
    setSelectedState(info.state);
    setHoveredUnit({
      unitId,
      name: info.name,
      city: info.city,
    });
  }

  function onPointerOutUnit(): void {
    if (!prevUnit.current) {
      setScope('state');
      setHoveredUnit(undefined);
    }
  }

  function clearStateFields(): void {
    const foundState = filterOptions.states.find((item) => item.label === selectedState)?.value as string;
    onSelectOption('state', foundState);

    setSelectedState(undefined);
    setHoveredState(undefined);
  }

  function clearUnitFields(): void {
    setSelectedUnit(undefined);
    setHoveredUnit(undefined);
    prevUnit.current = undefined;
  }

  function onCloseInfoCard(): void {
    if (scope === 'state') {
      if (selectedState && hasOnlyCurrentStateData()) {
        clearFilter('healthStatus');
      }
      clearStateFields();
      goCountryMap();
    } else {
      onCloseUnitCard();
      handleScope('state');
      clearUnitFields();
    }
  }

  function hasOnlyCurrentStateData(): boolean {
    return unitsStateMap.size === 1;
  }

  function onClickResetButton(): void {
    if (prevUnit.current) {
      onCloseUnitCard();
      clearUnitFields();
    }
    clearStateFields();
    clearFilter('state');
    handleScope('state');
  }

  function buildUnitMap(units: UnitsInfo): Map<string, UnitsInfo> {
    const groupMap = new Map<string, typeof units>();

    for (const unit of units) {
      if (!unit.lat || !unit.lon) {
        const emptyUnits = groupMap.get('empty') ?? [];
        emptyUnits.push(unit);

        groupMap.set('empty', emptyUnits);
        continue;
      }

      const stateUnits = groupMap.get(unit.state) ?? [];
      stateUnits.push(unit);

      groupMap.set(unit.state, stateUnits);
    }

    return groupMap;
  }

  function filterUnitByOptions(unitInfo: UnitsInfo[number]): boolean {
    const conditions: boolean[] = [];

    if (selectedOptions.group) {
      conditions.push(selectedOptions.group.units.includes(unitInfo.id));
    }

    if (selectedOptions.healthStatus) {
      conditions.push(selectedOptions.healthStatus === unitInfo.health);
    }

    if (selectedOptions.state?.length === 1) {
      const foundState = filterOptions.states.find(
        (item) => item.value === selectedOptions.state?.[0],
      );
      conditions.push(foundState?.label === unitInfo.state);
    }

    return conditions.every(Boolean);
  }

  function filterUnitMap(allMap?: Map<string, UnitsInfo>): Map<string, UnitsInfo> {
    const newMap = new Map<string, UnitsInfo>();
    const map = allMap ?? allUnitsStateMap;

    for (const [key, value] of map) {
      const filteredValues = value.filter(filterUnitByOptions);
      newMap.set(key, filteredValues);
    }

    return newMap;
  }

  function setFilterStateOnMap(): void {
    if (selectedOptions.state?.length === 1 && !selectedState) {
      const [stateId] = selectedOptions.state;
      const foundState = filterOptions.states.find((item) => item.value === stateId)?.label as string;
      setSelectedState(foundState);
    }

    if (selectedOptions.state && selectedOptions.state.length > 1 || (selectedState && selectedOptions.state?.length === 0)) {
      if (selectedState) {
        setSelectedState(undefined);
      }
      goCountryMap();
    }
  }

  function getMapInfoName(): string | undefined {
    if (debouncedScope === 'state') {
      return debouncedSelectedState ?? debouncedHoverState;
    }

    return debouncedSelectedUnit?.name ?? debouncedHoverUnit?.name;
  }

  function getUnitInfo(): { id: number, city: string } | undefined {
    if (debouncedScope === 'state') return undefined;

    if (debouncedSelectedUnit) {
      return {
        city: debouncedSelectedUnit.city,
        id: Number(debouncedSelectedUnit.unitId),
      };
    }

    if (debouncedHoverUnit) {
      return {
        city: debouncedHoverUnit.city,
        id: Number(debouncedHoverUnit.unitId),
      };
    }

    return undefined;
  }

  function handleFilterChanges(): void {
    const clientHasChanged = prevClient.current !== client;
    const groupHasChanged = prevGroup.current !== selectedOptions.group?.value;
    const healthHasChanged = prevHealth.current !== selectedOptions.healthStatus;
    const stateHasChanged = !sameStatesArray(prevState.current ?? [], selectedOptions.state ?? []);

    const hasChanged = clientHasChanged || groupHasChanged || healthHasChanged || stateHasChanged;

    if (clientHasChanged) {
      prevClient.current = client;
    }

    if (groupHasChanged) {
      prevGroup.current = selectedOptions.group?.value;
    }

    if (healthHasChanged) {
      prevHealth.current = selectedOptions.healthStatus;
    }

    if (stateHasChanged) {
      prevState.current = selectedOptions.state;
    }

    const groupMap = buildUnitMap(units);
    let needGroupMap = false;

    if (units.length > 0 && (firstRender.current || clientHasChanged)) {
      needGroupMap = true;
      setAllUnitsStateMap(groupMap);
    }

    const filteredMap = filterUnitMap(needGroupMap ? groupMap : undefined);

    if (firstRender.current || hasChanged) {
      if (firstRender.current) {
        firstRender.current = false;
      }

      setUnitsStateMap(filteredMap);
      updateUnitsMap(filteredMap);
    }
  }

  useEffect(() => {
    setFilterStateOnMap();
  }, [selectedOptions.state]);

  useEffect(() => {
    if (!units) return;
    handleFilterChanges();
  }, [units]);

  useEffect(() => {
    unitsStateMapReference.current = unitsStateMap;
  }, [unitsStateMap]);

  useEffect(() => () => {
    unitsStateMapReference.current = new Map();
    prevClient.current = undefined;
    prevGroup.current = undefined;
    prevHealth.current = undefined;
    prevUnit.current = undefined;
    firstRender.current = true;
  }, []);

  return (
    <MapContentContainer>
      <MapChart />
      <MapInfo
        name={getMapInfoName()}
        unitInfo={getUnitInfo()}
        onCloseInfoCard={onCloseInfoCard}
        scope={debouncedScope}
        selected={debouncedScope === 'state' ? !!debouncedSelectedState : !!debouncedSelectedUnit}
        unitsMap={unitsStateMap}
        state={debouncedScope === 'unit' ? debouncedSelectedState : undefined}
      />
    </MapContentContainer>
  );
}
